import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ImagePlaceholder } from '../components/ImagePlaceholder';
interface AppByPhoneProps {
  title: string;
}
const placeholderText = 'text-stone-medium leading-relaxed italic';
export function AppByPhone({
  title
}: AppByPhoneProps) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const container = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1
      }
    }
  };
  const item = {
    hidden: {
      opacity: 0,
      y: 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.19, 1, 0.22, 1]
      }
    }
  };
  return <motion.article variants={container} initial="hidden" animate="show" className="w-full pb-[0px]" data-id="element-158" data-mp="2ztj3hz175dtn">
      {/* Header Section */}
      <div className="max-w-4xl mx-auto px-6 md:px-12 mb-16 md:mb-24 text-center pt-12" data-id="element-159" data-mp="130hjcfr2b0ul">
        <motion.h1 variants={item} className="text-4xl md:text-6xl font-bold text-ink tracking-tight mb-6" data-id="element-160" data-mp="y2pgiqovpubm">
          {title}
        </motion.h1>
        <motion.p variants={item} className="text-xl md:text-2xl text-stone-medium font-light leading-relaxed max-w-3xl mx-auto mb-16" data-id="element-161" data-mp="2sjjio2ll7rtu">
          Helping loan agents guide borrowers through complex loan applications.
        </motion.p>

        {/* Metadata Row */}
        <motion.div variants={item} className="grid grid-cols-1 md:grid-cols-3 gap-8 text-left border-t border-b border-stone-border py-8" data-id="element-162" data-mp="3785ye5f4mlll">
          <div data-id="element-163" data-mp="3b8zl4vgu1ucw">
            <h3 className="text-sm font-bold text-ink mb-2" data-id="element-164" data-mp="3bblsov3q49v4">Company</h3>
            <p className="text-stone-medium text-sm italic" style={{
            fontWeight: "400",
            fontStyle: "normal"
          }} data-id="element-165" data-mp="2xhutognmc8dn">
              Upgrade
            </p>
          </div>
          <div data-id="element-166" data-mp="3q6rypi2tk8og">
            <h3 className="text-sm font-bold text-ink mb-2" data-id="element-167" data-mp="3q69xe71av49o">My role</h3>
            <p className="text-stone-medium text-sm italic" style={{
            fontStyle: "normal"
          }} data-id="element-168" data-mp="3uy9zo64ue99f">
              Product Designer
            </p>
          </div>
          <div data-id="element-169" data-mp="8m9zhryayv6j">
            <h3 className="text-sm font-bold text-ink mb-2" data-id="element-170" data-mp="3p9tti5d7ufy">Platform</h3>
            <p className="text-stone-medium text-sm" data-id="element-171" data-mp="3q1isa4gitx9">
              Desktop web (internal tool)
            </p>
          </div>
        </motion.div>
      </div>

      {/* Hero Image */}
      <motion.div variants={item} className="w-full mb-16 md:mb-24 flex justify-center" data-id="element-172" data-mp="3pvyc6pbs2mr3">
        <img src="https://cdn.magicpatterns.com/uploads/er8jPabYGzGyvKw6mHQSpY/ABP.png" alt="App by Phone agent desktop workflow shown on a monitor" className="w-full h-auto block object-contain" data-id="element-173" data-mp="3lw7fk4zklcen" />
      </motion.div>

      {/* Context and Challenge */}
      <div className="max-w-3xl mx-auto px-6 md:px-12 mb-24 space-y-16" data-id="element-174" data-mp="377jjzypq52op">
        <motion.section variants={item} data-id="element-175" data-mp="325uawom2az39">
          <h2 className="text-2xl font-bold text-ink mb-4" data-id="element-176" data-mp="328zwhtvhfiu8">The Challenge</h2>
          <p className="text-stone-medium leading-relaxed mb-6" data-id="element-177" data-mp="2y5tuwzjbefu3">
            Applying for a personal loan involves multiple steps, from providing personal information and selecting an offer to verifying financial details.

When borrowers need assistance, loan agents must guide them through the process while answering questions and managing the application in real time.

          </p>
          <p className={placeholderText} style={{
          fontWeight: "500"
        }} data-id="element-178" data-mp="1rlpsvbejub5d">
            The challenge was to transform a self-service application into an agent-assisted experience without disrupting the conversation.
          </p>
        </motion.section>
        <motion.section variants={item} data-id="element-179" data-mp="13xgcwq40ohm5">
          <h2 className="text-2xl font-bold text-ink mb-4" data-id="element-180" data-mp="13wew050bl0wi">Mapping the Workflow</h2>
          <p className={placeholderText} style={{
          fontStyle: "normal"
        }} data-id="element-181" data-mp="18og6pmhuajkf">
            I reviewed business requirements, mapped the existing application flow, and shadowed support agents to understand how they assisted borrowers over the phone. The goal was to preserve the essential application steps while adapting the experience to how agents actually work—navigating information, answering questions, and moving borrowers forward.
          </p>
          <div className="mt-6 w-full" data-id="element-182" data-mp="2dtd35a0wvo8c">
            <img src="https://cdn.magicpatterns.com/uploads/rWT6ByUjh3pBn5WzNToswV/Userflow.png" alt="Application user flow: new application steps 1–5 and resume via search and authenticate" className="relative left-1/2 -translate-x-1/2 w-[130%] max-w-[90vw] h-auto object-contain" data-id="element-183" data-mp="2rne6kh2t5zf1" />
          </div>
        </motion.section>
      </div>

      {/* Agent Workflow — Full Bleed */}
      <motion.section variants={item} className="mb-32" data-id="element-184" data-mp="3akf81rx5lhe8">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center mb-12" data-id="element-185" data-mp="35mvnka9v48du">
          <h2 className="text-3xl font-bold text-ink mb-4" data-id="element-186" data-mp="35ksz3uocynb8">Design Solution</h2>
          <p className="text-stone-medium mb-8 italic" style={{
          fontStyle: "normal"
        }} data-id="element-187" data-mp="1g7xtd8qdrpl7">
            A unified workspace for borrower information, application steps, and contextual guidance.
          </p>
        </div>
        <div className="w-full bg-[#F3F4F5] py-12" style={{
        backgroundColor: "#E3F1EB",
        backgroundImage: "none"
      }} data-id="element-188" data-mp="3myamz7p28efg">
          <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-189" data-mp="3e3w7p54j53wc">
            <video src="https://cdn.magicpatterns.com/uploads/qzuefwgWVLfbHHWzjFJtKY/Scene-1%20(3).mp4" autoPlay loop muted playsInline className="w-full h-auto block mx-auto rounded-md" data-id="element-190" data-mp="3e3e4tpnd3rfg" />
          </div>
        </div>
      </motion.section>

      {/* Text + Image Modules */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 mb-32 space-y-32" data-id="element-191" data-mp="395clr4pxrs3z">
        <motion.section variants={item} className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center" data-id="element-192" data-mp="2v5a4nkpgd2ps">
          <div className="lg:col-span-4" data-id="element-193" data-mp="2z6vqhvjysrc4">
            <h2 className="text-2xl font-bold text-ink mb-4" style={{
            fontWeight: "700"
          }} data-id="element-194" data-mp="tdf8yejld8t7">
              Guidance in context
            </h2>
            <p className={placeholderText} style={{
            fontStyle: "normal"
          }} data-id="element-195" data-mp="xdpg3uj4e59n">
              A contextual information panel gives agents quick access to explanations and talking points.
            </p>
          </div>
          <div className="lg:col-span-8 w-full" data-id="element-196" data-mp="21bb2g36p6rl4">
            <img src="https://cdn.magicpatterns.com/uploads/3cx1q18B1cEZ8St5uDrKZ4/Helper_Text.gif" alt="Helper text appearing on form fields to guide agents" className="w-full h-auto object-contain mx-auto rounded-md" data-id="element-197" data-mp="1xazk4x1t53ct" />
          </div>
        </motion.section>

        <motion.section variants={item} className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center" style={{
        marginTop: "64px"
      }} data-id="element-198" data-mp="2l0lftaiz10g2">
          <div className="lg:col-span-4" data-id="element-199" data-mp="2g8aga8s57go1">
            <h2 className="text-2xl font-bold text-ink mb-4" data-id="element-200" data-mp="2bba5xlo9frr4">
              Keep legal content accessible
            </h2>
            <p className={placeholderText} style={{
            fontStyle: "normal"
          }} data-id="element-201" data-mp="38rhe8qhxmtwa">
              Reveal longer disclosures on demand without overwhelming the primary task.
            </p>
          </div>
          <div className="lg:col-span-8 lg:order-first w-full" data-id="element-202" data-mp="3bbvscibabbkc">
            <video src="https://cdn.magicpatterns.com/uploads/ufQ7zS3Drfk8nSw4gYnQsL/Helper%20Text%202.mov" autoPlay loop muted playsInline className="w-full h-auto object-contain block mx-auto rounded-md" data-id="element-203" data-mp="3g9oyjbcl85km" />
          </div>
        </motion.section>
      </div>

      {/* Impact and outcome */}
      <motion.section variants={item} className="w-full bg-ink text-white py-32 px-6 md:px-12" data-id="element-204" data-mp="3u9q06t0ofh15">
        <div className="max-w-4xl mx-auto text-center" data-id="element-205" data-mp="32vq65nzv27x">
          <h2 className="text-3xl md:text-4xl font-bold mb-8" data-id="element-206" data-mp="31c581re82zt">
            Impact and outcome
          </h2>
          <p className="text-xl text-stone-medium font-light leading-relaxed italic mb-16" style={{
          fontStyle: "normal"
        }} data-id="element-207" data-mp="7whsb3wj7nph">
            Validated by support agents
          </p>
          <div className="max-w-md mx-auto" data-id="element-208" data-mp="gwmwz6acc7zy">
            <div className="text-5xl font-bold mb-2" data-id="element-209" data-mp="ltdely1snbnh">9.1/10</div>
            <div className="text-sm text-stone-medium uppercase tracking-wider" data-id="element-210" data-mp="lrk4g8rz29aq">
              AVERAGE SATISFACTION RATING FROM 7 SUPPORT AGENTS.
            </div>
          </div>
        </div>
      </motion.section>
    </motion.article>;
}